import React, {
    Fragment,
    useEffect,
    useState,
    useCallback,
    useMemo,
} from 'react';
import { connect, useSelector } from 'react-redux';
import { Scrollbars } from 'react-custom-scrollbars';

import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { getDepId } from '../../utilities/Utils';
import { checkResStatusService } from '../../utilities/ResServiceUtils';
import { convertDateDisplay, formatDate } from '../../utilities/DatetimeUtils';
import { PERMISSION_ORG, SUB_FUNCTION } from '../../constants/APDConstants';
import * as AlertMSGConstants from '../../constants/AlertMSGConstants';

import Loading from '../../control/loading/Loading';
import UseLoadingState from '../../control/loading/UseLoadingState';
import AlertMessage from '../../control/alert/AlertMessage';
import UseAlertMessageState from '../../control/alert/UseAlertMessageState';
import DataTable from '../../control/table/DataTable';

import GetAppIcon from '@material-ui/icons/GetApp';

import SearchForm from './SearchForm';
import { getFormValues } from 'redux-form';

import moment from 'moment';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import CustomButton from '../../control/button/Button';

import * as ApiEvent from '../../api/ApiEvent';
import { constructEventDateTimeString, formatDisplayNumber } from './FormUtils';

const columns = [
    {
        title: 'ชื่อกิจกรรม',
        data: 'eventName',
        align: 'left',
        sort: true,
        width: '15%',
    },
    {
        title: 'สถานที่จัด',
        data: 'venue',
        sort: true,
        width: '10%',
    },
    {
        title: 'ผู้ประสานงานหลัก',
        data: 'mainCoordinator',
        sort: true,
        width: '10%',
    },
    {
        title: 'สถานะกิจกรรม',
        data: 'statusName',
        type: 'string',
        sort: true,
        width: '10%',
    },
    {
        title: 'จำนวนผู้ลงทะเบียนล่วงหน้า',
        data: 'reservedCount',
        sort: true,
        width: '10%',
        align: 'center',
    },
    {
        title: 'การเผยแพร่กิจกรรม',
        data: 'isPublic',
        sort: true,
        align: 'left',
        width: '8%',
    },
    {
        title: 'วันที่เริ่มต้นกิจกรรม',
        data: 'eventStartDate',
        type: 'date',
        sort: true,
        width: '10%',
    },
    {
        title: 'วันที่สิ้นสุดกิจกรรม',
        data: 'eventEndDate',
        sort: true,
        width: '10%',
    },
    {
        title: 'วันที่เปิดลงทะเบียนล่วงหน้า',
        data: 'reserveStartDate',
        sort: true,
        width: '10%',
    },
    {
        title: 'วันที่สิ้นสุดการลงทะเบียนล่วงหน้า',
        data: 'reserveEndDate',
        sort: true,
        width: '10%',
    },
    {
        title: 'วันที่แก้ไขล่าสุด',
        data: 'updatedAt',
        sort: true,
        width: '10%',
    },
];

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        height: '100%',
    },
    rootPaper: {
        minHeight: 'calc(100% - 48px)',
        margin: '24px',
        [theme.breakpoints.down('sm')]: {
            width: 'calc(98% - 48px)',
        },
        ...theme.paperPadding,
    },
    form: {
        marginTop: theme.spacing(2),
    },
    divider: {
        width: '100%',
        margin: '24px 0px',
    },
    result: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '20px',
        [theme.breakpoints.down('sm')]: {
            justifyContent: 'flex-start',
            paddingTop: '8px',
            paddingBottom: '8px',
        },
    },
    buttonExportAll: {
        height: '40px',
        marginBottom: '5px',
        padding: '0px',
        marginLeft: '10px',
        paddingRight: '10px',
        paddingLeft: '10px',
        backgroundColor: '#016f01 !important',
        color: '#FFFFFF',
    },
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    modalBody: {
        padding: theme.spacing(3),
    },
    modalLabel: {
        fontWeight: 'bold',
    },
    modalItemTable: {
        minWidth: 'auto',
    },
    btnCloseModal: {
        backgroundColor: `${theme.mainTheme.color6} !important`,
    },
    tableControl: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
}));

const defaultValues = {
    eventName: null, //ชื่อกิจกรรม
    isPublic: null, // การเผยแพร่กิจกรรม
    coordinators: null, // ผู้ประสานงานหลัก
    isUnlimitedCapacity: null, // ต้องมาเอาออกที่หลังอาจจะไม่ได้ใช้แล้ว
    statusIds: [], // สถานะกิจกรรม
    eventStartDate: null, // วันที่เริ่มกิจกรรม
    eventEndDate: null, // วันที่สิ้นสุดกิจกรรม
    reserveStartDate: null, // วันที่เริ่มต้นลงทะเบียนล่วงหน้า
    reserveEndDate: null, // วันที่สิ้นสุดลงทะเบียนล่วงหน้า
};

const formId = 'EventSearchForm';

const formatEventDateTime = (date) => {
    return date
        ? convertDateDisplay(formatDate(new Date(date))) +
              ' ' +
              moment(date).format('HH:mm น.')
        : '-';
};

const EventManagementComponent = ({ authenRdc }) => {
    const history = useHistory();
    const values = useSelector((state) => getFormValues(formId)(state));
    const classes = useStyles();
    const alertMessageState = UseAlertMessageState();
    const loadingState = UseLoadingState();
    const [tableConfig, setTableConfig] = useState({
        currentPage: 1,
        pageSize: 10,
        sortField: null,
        sortOrder: null,
        total: 0,
    });
    const [filters, setFilters] = useState({ ...defaultValues });
    const [results, setResults] = useState([]);

    const depId = useMemo(() => getDepId(authenRdc.profile), [authenRdc]);

    const canCreateEvent = useMemo(() => {
        return authenRdc.profile.subFunctions.some(
            (fn) =>
                fn.subFunctionId === SUB_FUNCTION.EVENT_CREATE ||
                fn.subFunctionId === SUB_FUNCTION.EVENT_ADMIN
        );
    }, [authenRdc]);

    const getLookups = useCallback(async () => {
        loadingState.showLoading();
        try {
            // Get Lookup for AutoComplete Fields here
            loadingState.hideLoading();
        } catch (error) {
            loadingState.hideLoading();
            alertMessageState.showAlert({
                altMsg: AlertMSGConstants.SYSTEM_ERROR,
            });
        }
    }, [authenRdc, loadingState, alertMessageState, depId]);

    const fetchData = useCallback(
        async (newTableConfig = {}, newFilters = {}) => {
            loadingState.showLoading();
            setFilters({
                ...filters,
                ...newFilters,
            });
            const parameters = {
                ...tableConfig,
                ...newTableConfig,
                ...filters,
                ...newFilters,
            };
            delete parameters.total;
            if (parameters.eventStartDate) {
                parameters.eventStartDate = moment(
                    parameters.eventStartDate
                ).format('YYYY-MM-DD');
            }
            if (parameters.eventEndDate) {
                parameters.eventEndDate = moment(
                    parameters.eventEndDate
                ).format('YYYY-MM-DD');
            }
            if (parameters.reserveStartDate) {
                parameters.reserveStartDate = moment(
                    parameters.reserveStartDate
                ).format('YYYY-MM-DD');
            }
            if (parameters.reserveEndDate) {
                parameters.reserveEndDate = moment(
                    parameters.reserveEndDate
                ).format('YYYY-MM-DD');
            }
            if (
                parameters.statusIds &&
                Array.isArray(parameters.statusIds) &&
                parameters.statusIds.length > 0
            ) {
                parameters.statusIds = parameters.statusIds.map(
                    (obj) => obj.id
                );
            } else {
                delete parameters.statusIds;
            }
            try {
                const response = await ApiEvent.fetchEventSearch(
                    parameters,
                    authenRdc.profile.uuid
                );
                // fetchData to service event here
                loadingState.hideLoading();
                if (response.data.items) {
                    const startIndex =
                        parameters.pageSize * (parameters.currentPage - 1) + 1;
                    setResults(
                        response.data.items.map((item, index) => ({
                            ...item,
                            detail: true,
                            edit: item.canEdit,
                            delete: item.canCancel,
                            index: startIndex + index,
                            eventStartDate: constructEventDateTimeString(
                                item.eventStartDate,
                                item.eventStartTime
                            ),
                            eventEndDate: constructEventDateTimeString(
                                item.eventEndDate,
                                item.eventEndTime
                            ),
                            reserveStartDate: constructEventDateTimeString(
                                item.reserveStartDate,
                                item.reserveStartTime
                            ),
                            reserveEndDate: constructEventDateTimeString(
                                item.reserveEndDate,
                                item.reserveEndTime
                            ),
                            updatedAt: formatEventDateTime(item.updatedAt),
                            mainCoordinator: item.mainCoordinator,
                            isPublic: item.isPublic
                                ? 'เปิดเผยสาธารณะ'
                                : 'เฉพาะผู้จัดกิจกรรม/ตามรายชื่อที่กำนหด',
                            reservedCount: `${formatDisplayNumber(
                                item.reservedCount
                            )}/${
                                item.totalCapacity === null
                                    ? 'ไม่กำหนด'
                                    : formatDisplayNumber(item.totalCapacity)
                            }`,
                        }))
                    );
                    setTableConfig((prev) => ({
                        ...prev,
                        ...newTableConfig,
                        total: response.data.total,
                    }));
                } else {
                    alertMessageState.showAlert({
                        altMsg:
                            checkResStatusService(response) ||
                            AlertMSGConstants.SYSTEM_ERROR,
                    });
                }
            } catch (error) {
                loadingState.hideLoading();
                alertMessageState.showAlert({
                    altMsg: AlertMSGConstants.SYSTEM_ERROR,
                });
            }
        },
        [
            alertMessageState,
            authenRdc.profile.uuid,
            filters,
            loadingState,
            tableConfig,
        ]
    );

    const exportData = useCallback(
        async (newFilters) => {
            loadingState.showLoading();
            setFilters({
                ...filters,
                ...newFilters,
            });
            const parameters = {
                ...tableConfig,
                ...filters,
                ...newFilters,
            };
            if (parameters.eventStartDate) {
                parameters.eventStartDate = moment(
                    parameters.eventStartDate
                ).format('YYYY-MM-DD');
            }
            if (parameters.eventEndDate) {
                parameters.eventEndDate = moment(
                    parameters.eventEndDate
                ).format('YYYY-MM-DD');
            }
            if (parameters.reserveStartDate) {
                parameters.reserveStartDate = moment(
                    parameters.reserveStartDate
                ).format('YYYY-MM-DD');
            }
            if (parameters.reserveEndDate) {
                parameters.reserveEndDate = moment(
                    parameters.reserveEndDate
                ).format('YYYY-MM-DD');
            }
            if (
                parameters.statusIds &&
                Array.isArray(parameters.statusIds) &&
                parameters.statusIds.length > 0
            ) {
                parameters.statusIds = parameters.statusIds.map(
                    (obj) => obj.id
                );
            } else {
                delete parameters.statusIds;
            }
            delete parameters.total;
            delete parameters.pageSize;
            delete parameters.currentPage;
            try {
                // export data in service event here
                const response = await ApiEvent.exportEventSearch(
                    authenRdc.profile.uuid,
                    parameters
                );
                loadingState.hideLoading();
                if (response) {
                    const url = window.URL.createObjectURL(response);
                    const link = document.createElement('a');
                    link.href = url;

                    link.setAttribute('download', 'ข้อมูลกิจกรรม.xlsx');
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                } else {
                    alertMessageState.showAlert({
                        altMsg:
                            // checkResStatusService(response) ||
                            AlertMSGConstants.SYSTEM_ERROR,
                    });
                }

                loadingState.hideLoading();
            } catch (error) {
                loadingState.hideLoading();
                alertMessageState.showAlert({
                    altMsg: AlertMSGConstants.SYSTEM_ERROR,
                });
            }
        },
        [
            alertMessageState,
            authenRdc.profile.uuid,
            filters,
            loadingState,
            tableConfig,
        ]
    );

    const fetchItemDetails = useCallback(
        async (item) => {
            loadingState.showLoading();
            const parameters = {
                uuid: item.uuid,
                workingDate: item.actualDate,
            };
            delete parameters.total;
            try {
                loadingState.hideLoading();
            } catch (error) {
                loadingState.hideLoading();
                alertMessageState.showAlert({
                    altMsg: AlertMSGConstants.SYSTEM_ERROR,
                });
            }
        },
        [alertMessageState, loadingState]
    );

    useEffect(() => {
        getLookups();
        fetchData({ currentPage: 1 });
    }, []);

    const handleClickExport = useCallback(() => {
        exportData(values);
    }, [exportData, values]);

    const handleSubmitSearchForm = useCallback(
        (values) => {
            fetchData({ currentPage: 1 }, { ...values });
        },
        [fetchData]
    );

    const handleSortChange = useCallback(
        (sortField, sortOrder) => {
            fetchData({
                currentPage: 1,
                sortField,
                sortOrder,
            });
        },
        [fetchData]
    );

    const handlePageChange = useCallback(
        (currentPage) => {
            fetchData({ currentPage });
        },
        [fetchData]
    );

    const handleClickCancelEvent = useCallback(
        async (eventId) => {
            // call cancel Api
            try {
                const res = await ApiEvent.putEventCancel(eventId, {
                    cancelBy: authenRdc.profile.uuid,
                });
                if (res.data.eventId) {
                    alertMessageState.showAlert({
                        altMsg: 'ยกเลิกกิจกรรมสำเร็จ',
                        callbackCancel: () => {
                            alertMessageState.hideAlert();
                        },
                    });
                }
            } catch (error) {
                alertMessageState.showAlert({
                    altMsg: AlertMSGConstants.SYSTEM_ERROR,
                });
            }
        },
        [alertMessageState, authenRdc]
    );

    const handleRowClick = useCallback(
        (item, mode) => {
            fetchItemDetails(item);
            if (mode === 'detail') {
                history.push(`/event/${item.eventId}/detail`);
            } else if (mode === 'edit') {
                history.push(`/event/${item.eventId}/edit`);
            } else if (mode === 'delete') {
                alertMessageState.showAlert({
                    altMsg: `คุณต้องการยกเลิกกิจกรรม ${item.eventName} ?`,
                    altType: 2,
                    callbackCancel: () => alertMessageState.hideAlert(),
                    callbackConfirm: async () => {
                        //call save api
                        alertMessageState.hideAlert();
                        loadingState.showLoading();
                        await handleClickCancelEvent(item.eventId);
                        await fetchData();
                        loadingState.hideLoading();
                    },
                });
            }
        },
        [
            fetchItemDetails,
            history,
            loadingState,
            fetchData,
            alertMessageState,
            handleClickCancelEvent,
        ]
    );

    const handleClickCreateEvent = useCallback(() => {
        history.push(`/event/create`);
    }, [history]);

    return (
        <Fragment>
            <Scrollbars universal={true}>
                <Paper className={classes.rootPaper}>
                    <Grid container justify="center">
                        <Grid item xs={12} className={classes.form}>
                            <SearchForm
                                defaultValues={defaultValues}
                                showOrganization={
                                    authenRdc.profile.permissionOrgId !==
                                    PERMISSION_ORG.PERSONAL_ORG
                                }
                                onSubmit={handleSubmitSearchForm}
                            />
                        </Grid>
                        <Grid item xs={12} className={classes.divider}>
                            <Divider />
                        </Grid>
                        <Grid item className={classes.tableControl}>
                            <div>
                                <Typography variant="h5" component="span">
                                    ข้อมูลกิจกรรม
                                </Typography>
                                <Button
                                    variant="contained"
                                    className={classes.buttonExportAll}
                                    startIcon={<GetAppIcon />}
                                    onClick={handleClickExport}
                                >
                                    ออกรายงาน EXCEL
                                </Button>
                            </div>
                            <div className={classes.result}>
                                <div>
                                    พบผลลัพธ์ทั้งหมด {tableConfig.total} รายการ
                                </div>
                                {canCreateEvent && (
                                    <CustomButton
                                        label="เพิ่มกิจกรรม"
                                        onClick={handleClickCreateEvent}
                                    />
                                )}
                            </div>
                        </Grid>
                        <Grid item xs={12}>
                            <DataTable
                                column={columns}
                                rowPerPage={tableConfig.pageSize}
                                notSort={true}
                                page={tableConfig.currentPage}
                                data={results}
                                total={tableConfig.total}
                                sortColumn={tableConfig.sortField}
                                sortType={tableConfig.sortOrder}
                                handleSortExternal={handleSortChange}
                                handlePageChange={handlePageChange}
                                handleRowClick={handleRowClick}
                                minWidth="2500px"
                                labelBtnDelete="ยกเลิก"
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Scrollbars>
            <AlertMessage {...alertMessageState.altProps} />
            <Loading isShowLoading={loadingState.isShowLoading} />
        </Fragment>
    );
};

export default connect((state) => ({
    authenRdc: state.authen,
}))(EventManagementComponent);
